/**
 * Profile tab data resource (CT-5286).
 *
 * The rows behind a DYNAMIC tab. Overview is the only tab that paints a
 * dashboard of widgets; every other tab lists data with filters and search, and
 * this is what feeds them.
 *
 * Rows come back in the same normalized card contract the entity list uses, so
 * one card component serves both screens. The FILTER DEFINITIONS come down with
 * them: which filters a tab offers is the tenant's business, so there is no
 * filter list anywhere in the frontend.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export { __resetMockTabDataForTests } from './__mocks__/profileTabData.mock.js';

/**
 * Map a raw filter definition onto the shape `Filters` expects for a slot.
 *
 * `options` is always an array so the toolbar never has to guard, and a filter
 * the backend sends with no options renders as an empty slot rather than
 * throwing.
 */
export const mapApiTabFilterToUi = (raw = {}) => ({
  id: raw.filter_id ?? raw.filterId ?? raw.id ?? '',
  label: raw.label ?? raw.name ?? '',
  options: (raw.options ?? []).map((option) => ({
    value: option.value ?? option.id ?? '',
    label: option.label ?? option.name ?? '',
  })),
});

/**
 * TODO(CT-5286 / backend): `POST /v1/workspace/profiles/tabs/data/list` with
 * `{ entity_type, entity_id, tab_id, page, page_size, filters, search }`.
 */
export const listProfileTabData = async ({
  entityType,
  entityId,
  tabId,
  page = 1,
  pageSize = 10,
  orderBy = 'title',
  orderDirection = 'asc',
  filters,
  search,
} = {}) => {
  const response = await request('/v0/profile/tabs/data/list', {
    entity_type: entityType,
    entity_id: entityId,
    tab_id: tabId,
    page,
    page_size: pageSize,
    order_by: orderBy,
    order_direction: orderDirection,
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
  });
  return validateApiResponse(response, 'Failed to load this tab');
};

export { __resetMockActivityForTests } from './__mocks__/activity.mock.js';

const toPerson = (person) => (typeof person === 'string' ? person : (person?.name ?? ''));

/** `{ id, name, role }` for an author, or null — a note has one, an email has a sender. */
const toAuthor = (author) => {
  if (!author) return null;
  if (typeof author === 'string') return { id: null, name: author, role: null };
  return { id: author.id ?? null, name: author.name ?? '', role: author.role ?? null };
};

/**
 * Map one raw Activity item onto what the feed card renders (CT-5527).
 *
 * `status` may arrive as a bare string or as `{ label, variant }`; the card only
 * ever sees the object, so it never has to guard. Missing optional fields are
 * `null`/empty, not `''`, so a slot the tenant did not fill renders nothing
 * instead of empty chrome.
 */
export const mapApiActivityItemToUi = (raw = {}) => {
  const status = raw.status ?? null;

  return {
    id: raw.id ?? raw.entity_id ?? raw.entityId ?? '',
    kind: raw.kind ?? raw.type ?? '',
    direction: raw.direction ?? null,
    title: raw.title ?? raw.subject ?? null,
    occurredAt: raw.occurred_at ?? raw.occurredAt ?? null,
    status:
      typeof status === 'string'
        ? { label: status, variant: undefined }
        : status
          ? { label: status.label ?? '', variant: status.variant }
          : null,
    preview: raw.preview ?? raw.body_preview ?? raw.bodyPreview ?? null,
    aiSummary: raw.ai_summary ?? raw.aiSummary ?? null,
    sender: toPerson(raw.sender) || null,
    recipients: (raw.recipients ?? []).map(toPerson).filter(Boolean),
    // Notes (CT-5528). `canEdit` defaults to false: an absent permission is not a grant.
    author: toAuthor(raw.author),
    scope: raw.scope ?? null,
    canEdit: Boolean(raw.can_edit ?? raw.canEdit ?? false),
    // Tasks, calls and meetings on History (CT-5724).
    assignee: raw.assignee ?? null,
    dueAt: raw.due_at ?? raw.dueAt ?? null,
    context: raw.context ?? null,
    origin: raw.origin ?? null,
    recordLabel: raw.record_label ?? raw.recordLabel ?? null,
    phone: raw.phone ?? null,
    durationSeconds: raw.duration_seconds ?? raw.durationSeconds ?? null,
    agentTools: raw.agent_tools ?? raw.agentTools ?? null,
    tone: raw.tone ?? null,
    participants: raw.participants ?? null,
    loggedByMe: Boolean(raw.logged_by_me ?? raw.loggedByMe ?? false),
  };
};

/**
 * The Activity feed of one record (CT-5527). Same call as every other tab,
 * extended with a `quick_filter` rather than a new resource; the response
 * carries the `filters` that chip offers, because they differ per chip.
 *
 * TODO(CT-5527 / backend): `POST /v0/profile/tabs/data/list` with
 * `{ entity_type, entity_id, tab_id: 'activity', quick_filter, page, page_size,
 * order_by, order_direction, filters, search }`.
 */
export const listProfileActivity = async ({
  entityType,
  entityId,
  tabId = 'activity',
  quickFilter = 'all',
  page = 1,
  pageSize = 10,
  orderBy = 'occurred_at',
  orderDirection = 'desc',
  filters,
  search,
  focusEventId,
} = {}) => {
  // `focus_event_id` (CT-5724, ASSUMED — TODO(backend)): "Open in History" names
  // an event; the server answers the PAGE that holds it under this query (or the
  // page asked for when it is not there), with `meta.focus_event_id` and
  // `meta.focus_found`.
  const response = await request('/v0/profile/tabs/data/list', {
    entity_type: entityType,
    entity_id: entityId,
    tab_id: tabId,
    quick_filter: quickFilter,
    page,
    page_size: pageSize,
    order_by: orderBy,
    order_direction: orderDirection,
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
    ...(focusEventId ? { focus_event_id: focusEventId } : {}),
  });
  return validateApiResponse(response, 'Failed to load the activity');
};
